import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { dashboardPageSx } from '@/shared/styles/surfaces';
import { CreditsWalletSection } from '../components/CreditsWalletSection';
import { EarnRewardsSection } from '../components/EarnRewardsSection';
import { MerchandiseSection } from '../components/MerchandiseSection';
import { PhotosPanel } from '../components/PhotosPanel';
import { PhotoUploadDialog } from '../components/PhotoUploadDialog';
import { ReferralHistory, ReferralStatsGrid } from '../components/ReferralOverview';
import { RewardsToastProvider } from '../components/RewardsToast';
import { ShareInviteDrawer } from '../components/ShareInviteDrawer';
import { TrippyHeroBanner } from '../components/TrippyHeroBanner';
import { useRewardActivities, useRewardsSummary } from '../hooks/useGrowth';
import type { ActivityAction, PhotoType } from '../types';

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function GuideRewardsPage() {
  const { t } = useTranslation('rewards');
  const summary = useRewardsSummary();
  const activities = useRewardActivities();
  const [shareOpen, setShareOpen] = useState(false);
  const [merchOpen, setMerchOpen] = useState(false);
  const [photoType, setPhotoType] = useState<PhotoType | null>(null);

  const onAction = (action: ActivityAction) => {
    switch (action) {
      case 'SHARE':
        setShareOpen(true);
        break;
      case 'REQUEST_TSHIRT':
        setMerchOpen(true);
        break;
      case 'TRACK_TSHIRT':
        scrollToId('merch');
        break;
      case 'UPLOAD_SELFIE':
        setPhotoType('T_SHIRT_SELFIE');
        break;
      case 'UPLOAD_TRAVELLER_PHOTO':
        setPhotoType('TRAVELLER_PHOTO');
        break;
    }
  };

  return (
    <RewardsToastProvider>
      <Box sx={{ ...dashboardPageSx, display: 'grid', gap: { xs: 4, md: 5 } }}>
        {summary.isError && (
          <Alert severity="error" action={<Button onClick={() => summary.refetch()}>{t('page.retry')}</Button>}>
            {t('page.loadError')}
          </Alert>
        )}

        <TrippyHeroBanner
          summary={summary.data}
          loading={summary.isLoading}
          onShare={() => setShareOpen(true)}
          onExplore={() => scrollToId('earn')}
        />

        <ReferralStatsGrid stats={summary.data?.referrals} loading={summary.isLoading} />

        <CreditsWalletSection wallet={summary.data?.wallet} loading={summary.isLoading} />

        <EarnRewardsSection
          activities={activities.data}
          loading={activities.isLoading}
          error={activities.isError}
          onRetry={() => activities.refetch()}
          onAction={onAction}
        />

        <ReferralHistory />

        <MerchandiseSection
          requestOpen={merchOpen}
          onRequestOpenChange={setMerchOpen}
          onUploadSelfie={() => setPhotoType('T_SHIRT_SELFIE')}
        />

        <PhotosPanel onAddTravellerPhoto={() => setPhotoType('TRAVELLER_PHOTO')} />
      </Box>

      {summary.data?.referralLink && (
        <ShareInviteDrawer open={shareOpen} onClose={() => setShareOpen(false)} referralLink={summary.data.referralLink} />
      )}
      <PhotoUploadDialog type={photoType} onClose={() => setPhotoType(null)} />
    </RewardsToastProvider>
  );
}
